import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import { useState, useEffect } from "react"
import { usePostHog } from 'posthog-js/react'

import {
  type Body_login_login_access_token as AccessToken,
  loginLoginAccessToken,
  usersReadUserMe,
  usersRegisterUser,
  type UserPublic,
  type UserRegister,
} from "@/client"

const isLoggedIn = () => {
  return localStorage.getItem("access_token") !== null
}

const useAuth = () => {
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  
  // PostHog might not be available in development
  let posthog = null
  try {
    posthog = usePostHog()
  } catch (e) {
    // PostHog not available (development mode)
    posthog = null
  }
  
  const { data: user } = useQuery<UserPublic | null, Error>({
    queryKey: ["currentUser"],
    queryFn: usersReadUserMe,
    enabled: isLoggedIn(),
  })

  // Identify user with PostHog when user data is available (production only)
  useEffect(() => {
    if (user && posthog) {
      posthog.identify(user.id, {
        email: user.email,
        full_name: user.full_name,
        is_active: user.is_active,
        is_superuser: user.is_superuser,
      })
    }
  }, [user, posthog])

  const signUpMutation = useMutation({
    mutationFn: async (data: UserRegister) => {
      const response = await usersRegisterUser({ requestBody: data });
      return response;
    },

    onSuccess: () => {
      navigate("/login")
    },
    onError: (err: any) => {
      const errDetail = err.body?.detail || err.message
      setError(errDetail)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] })
    },
  })

  const login = async (data: AccessToken) => {
    const response = await loginLoginAccessToken({
      formData: data,
    })
    localStorage.setItem("access_token", response.access_token)
  }

  const loginMutation = useMutation({
    mutationFn: login,
    onSuccess: () => {
      // Track login event
      posthog?.capture('user_logged_in', {
        login_method: 'email_password'
      })
      navigate("/")
    },
    onError: (err: any) => {
      const errDetail = err.body?.detail || err.message
      setError(errDetail)
      // Track login failure
      posthog?.capture('login_failed', {
        error: errDetail
      })
    },
  })

  const logout = () => {
    // Track logout event
    posthog?.capture('user_logged_out')
    // Reset PostHog user identification
    posthog?.reset()
    localStorage.removeItem("access_token")
    navigate("/login")
  }

  return {
    signUpMutation,
    loginMutation,
    logout,
    user,
    error,
    resetError: () => setError(null),
  }
}

export { isLoggedIn }
export default useAuth